import {useCallback, useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import ImageGalleryV2 from '@amblique/king-living/app/components/image-gallery-v2';
import ViewDetails from '@amblique/king-living/app/components/configuration-selector/view-details';
import {resolveGalleryResourceForProduct} from '@amblique/king-living/app/utils/gallery-resource-utils';

/** Back-arrow icon — matches the flyout "back to configuration" button */
function BackArrowIcon() {
  return (
    <svg
      fill="none"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M8.50879 1.21854C8.80162 0.925821 9.27646 0.925884 9.56934 1.21854C9.86188 1.51142 9.86202 1.98628 9.56934 2.27907L2.55957 9.28778H18.5127C18.9267 9.288 19.2626 9.62378 19.2627 10.0378C19.2627 10.4518 18.9267 10.7875 18.5127 10.7878H2.55957L9.56934 17.7974L9.62109 17.8541C9.86114 18.1486 9.84375 18.5835 9.56934 18.858C9.29497 19.132 8.86087 19.1493 8.56641 18.9097L8.50879 18.858L0.219727 10.568C0.18835 10.5366 0.160394 10.5026 0.135742 10.4675C0.132269 10.4625 0.128354 10.4578 0.125 10.4528C0.115506 10.4385 0.109002 10.4226 0.100586 10.4079C0.0849364 10.3805 0.0687944 10.3533 0.0566406 10.3239C0.0499829 10.3078 0.046501 10.2906 0.0410156 10.2741C0.0161827 10.1995 0 10.1207 0 10.0378C1.99669e-05 9.95668 0.0152351 9.87947 0.0390625 9.80633C0.0445148 9.78951 0.0480151 9.77202 0.0546875 9.75555L0.0585938 9.74578C0.066117 9.72802 0.0771166 9.71204 0.0859375 9.695C0.120563 9.62794 0.163507 9.56372 0.219727 9.5075L8.50879 1.21854Z"
        fill="currentColor"
      />
    </svg>
  );
}

BackArrowIcon.displayName = 'BackArrowIcon';

/** Stable empty array — avoids creating new references on each render when product has no PIM images */
const EMPTY_THUMBNAIL = [];

const EMPTY_GALLERY_RESOURCE = {
  customerId: undefined,
  cylindoFeatures: [],
  cylindoId: undefined,
  isReady: false,
  pimImages: undefined,
};

/**
 * Restore page scroll after drawer closes on mobile.
 * Uses double RAF to avoid race condition with Chakra UI body unlock.
 *
 * @param {number} scrollY - Saved scroll position
 * @returns {Function} Cleanup function to cancel pending RAF
 */
function restoreScrollAfterBodyUnlock(scrollY) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let rafId1 = null;
  let rafId2 = null;

  rafId1 = requestAnimationFrame(() => {
    rafId2 = requestAnimationFrame(() => {
      try {
        window.scrollTo(0, scrollY);
      } catch {
        // Ignore: scrollTo may fail in some environments (e.g., test env, iframe restrictions)
      }
    });
  });

  return () => {
    if (rafId1 !== null) cancelAnimationFrame(rafId1);
    if (rafId2 !== null) cancelAnimationFrame(rafId2);
  };
}

/**
 * Shared bottom-sheet Drawer for displaying full product details on mobile.
 *
 * Renders a full-height `Drawer` containing:
 *   1. Back-arrow header with the product title
 *   2. Full `ImageGalleryV2` (PIM images, videos, Cylindo 3D viewer)
 *   3. `ViewDetails` for the PIM thumbnail swiper + title + short/long description
 *
 * Designed to be triggered by a mobile-only "See details" button.
 * Accepts a full SCAPI product object and resolves gallery props via
 * `resolveGalleryResourceForProduct` (dedicated `c_video` → pimImages → Cylindo),
 * matching desktop add-on "See details" and KINDW-1282 video priority.
 *
 * Usage:
 * ```jsx
 * <StandaloneViewDetailsModal
 *   isOpen={isOpen}
 *   product={selectedProduct}
 *   onClose={handleClose}
 * />
 * ```
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.onClose - Callback to close the drawer
 * @param {Object|null} [props.product] - Full SCAPI product object from useProducts()
 */
/** Unique ID for the drawer title, used by aria-labelledby */
const DRAWER_TITLE_ID = 'standalone-view-details-modal-title';

const StandaloneViewDetailsModal = ({isOpen, onClose, product = null}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('StandaloneViewDetailsModal', {});
  const scrollLockRef = useRef({y: 0});
  const cancelRestoreScrollRef = useRef(null);

  const title = product?.name ?? '';
  const description = product?.shortDescription;
  const longDescription = product?.longDescription;
  const thumbnail = Array.isArray(product?.c_pimlyConfigurationImage) ?
    product.c_pimlyConfigurationImage :
    EMPTY_THUMBNAIL;

  const galleryResource = useMemo(
    () => (product ? resolveGalleryResourceForProduct(product) : EMPTY_GALLERY_RESOURCE),
    [product]
  );

  useEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      scrollLockRef.current = {y: window.scrollY ?? document.documentElement.scrollTop};
    }
  }, [isOpen]);

  const handleCloseComplete = useCallback(() => {
    const {y} = scrollLockRef.current;

    cancelRestoreScrollRef.current = restoreScrollAfterBodyUnlock(y);
  }, []);

  useEffect(() => {
    return () => {
      cancelRestoreScrollRef.current?.();
    };
  }, []);

  return (
    <Drawer
      blockScrollOnMount={false}
      isOpen={isOpen}
      placement="bottom"
      size="full"
      onClose={onClose}
      onCloseComplete={handleCloseComplete}
    >
      <DrawerOverlay />
      <DrawerContent aria-labelledby={DRAWER_TITLE_ID}>
        {/* Header: back button + product title */}
        <Box className="standalone-view-details-modal__header" {...styles.header}>
          <Box
            alignItems="center"
            as="button"
            background="none"
            border="none"
            cursor="pointer"
            display="flex"
            padding={0}
            type="button"
            aria-label={intl.formatMessage({
              defaultMessage: 'Back',
              id: 'standalone_view_details_modal.back_aria',
            })}
            onClick={onClose}
          >
            <BackArrowIcon />
          </Box>
          <Text id={DRAWER_TITLE_ID} noOfLines={1} {...styles.headerTitle}>
            {title || intl.formatMessage({
              defaultMessage: 'Product Details',
              id: 'standalone_view_details_modal.title_fallback',
            })}
          </Text>
        </Box>

        {/* Body: image gallery → view-details */}
        <DrawerBody className="standalone-view-details-modal__body" {...styles.body}>
          {/* Full product gallery: video, PIM, Cylindo, origin images */}
          {product && (
            <Box className="standalone-view-details-modal__gallery" {...styles.gallery}>
              <ImageGalleryV2
                customerId={galleryResource.customerId}
                cylindoFeatures={galleryResource.cylindoFeatures}
                cylindoId={galleryResource.cylindoId}
                lazy={false}
                pimImages={galleryResource.pimImages}
                productName={title}
              />
            </Box>
          )}

          {/* Text content: PIM thumbnail swiper + title + descriptions */}
          <Box className="standalone-view-details-modal__details" {...styles.details}>
            <ViewDetails
              description={description}
              longDescription={longDescription}
              thumbnail={thumbnail}
              title={title}
            />
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

StandaloneViewDetailsModal.displayName = 'StandaloneViewDetailsModal';

StandaloneViewDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object,
};

export default StandaloneViewDetailsModal;
